import { GoogleGenerativeAI } from '@google/generative-ai';

const apiKey = process.env.REACT_APP_GEMINI_API_KEY || '';

if (!apiKey) {
  console.warn('REACT_APP_GEMINI_API_KEY is not set. Please add it to your .env file.');
}

export const genAI = apiKey ? new GoogleGenerativeAI(apiKey) : null;


export const extractDataFromPDF = async (file) => {
  if (!genAI) {
    throw new Error('Gemini API key is not configured. Please set REACT_APP_GEMINI_API_KEY in your .env file.');
  }

  const model = genAI.getGenerativeModel({ model: 'gemini-3-pro-preview' });

  // Convert file to base64 (browser-compatible)
  const arrayBuffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  let binaryString = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binaryString += String.fromCharCode(uint8Array[i]);
  }
  const base64Data = btoa(binaryString);
  const mimeType = file.type;

  // Create the prompt for data extraction
  const extractionPrompt = `You are a financial document analysis expert. Extract the following information from this financial document and return it as a JSON object. If a field is not found, set it to null.

Extract these fields with their exact values or appropriate categories:

1. Company Size (employees): Return one of these values: "1-10", "11-50", "51-200", "201-1000", "1000+", or null
2. Annual Revenue: Return one of these values: "<$1M", "$1M-$10M", "$10M-$50M", "$50M-$200M", "$200M+", or null
3. Years in Business: Return one of these values: "<1", "1-3", "3-10", "10+", or null
4. Geographic Location: Return one of these values: "Out of territory", "Secondary market", "Primary market", or null
5. Credit Rating/Score: Return one of these values: "Poor (<600)", "Fair (600-699)", "Good (700-749)", "Excellent (750+)", or null
6. Profitability Status: Return one of these values: "Losses", "Break-even", "Profitable", "Highly profitable", or null
7. Cash Flow: Return one of these values: "Negative", "Neutral", "Positive", or null
8. Debt Level: Return one of these values: "High", "Moderate", "Low", or null
9. Website Visits (last 30 days): Return one of these values: "0", "1-2", "3-5", "6-10", "11+", or null
10. Content Downloads: Return one of these values: "None", "1", "2-3", "4+", or null
11. Email Engagement: Return one of these values: "No opens", "Opens only", "Clicks", "Replies", or null
12. Demo/Trial Request: Return one of these values: "Yes", "No", or null
13. Purchase Timeline: Return one of these values: "No timeline", ">12 months", "6-12 months", "3-6 months", "<3 months", or null
14. Budget Status: Return one of these values: "No budget", "Budget needed", "Budget allocated", or null
15. Decision Maker Identified: Return one of these values: "No", "Influencer", "Decision maker", or null
16. Pain Level: Return one of these values: "Low", "Medium", "High", "Critical", or null
17. Competitive Situation: Return one of these values: "Loyal to competitor", "Evaluating options", "Dissatisfied with current", or null
18. Lifetime Value Potential: Return one of these values: "Low", "Medium", "High", "Very high", or null
19. Referral Source: Return one of these values: "Cold outbound", "Inbound", "Partner", "Customer referral", or null
20. Upsell/Cross-sell Potential: Return one of these values: "Low", "Medium", "High", or null
21. Strategic Account Value: Return one of these values: "Standard", "Important", "Strategic", or null

Return ONLY a valid JSON object with these exact keys (use camelCase):
{
  "companySize": "value or null",
  "revenue": "value or null",
  "yearsInBusiness": "value or null",
  "location": "value or null",
  "creditScore": "value or null",
  "profitability": "value or null",
  "cashFlow": "value or null",
  "debtLevel": "value or null",
  "websiteVisits": "value or null",
  "contentEngagement": "value or null",
  "emailEngagement": "value or null",
  "demoRequest": "value or null",
  "timeline": "value or null",
  "budget": "value or null",
  "authority": "value or null",
  "need": "value or null",
  "competition": "value or null",
  "ltv": "value or null",
  "referral": "value or null",
  "expansion": "value or null",
  "strategic": "value or null"
}

Do not include any additional text, explanations, or markdown formatting. Only return the JSON object.`;

  try {
    // Upload file to Gemini
    
    const result = await model.generateContent([
      extractionPrompt,
      {
        inlineData: {
          data: base64Data,
          mimeType: mimeType,
        },
      },
    ]);

    const response = await result.response;
    const text = response.text();

    // Extract JSON from response (handle cases where there might be markdown code blocks)
    let jsonText = text.trim();
    
    // Remove markdown code blocks if present
    if (jsonText.startsWith('```json')) {
      jsonText = jsonText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    } else if (jsonText.startsWith('```')) {
      jsonText = jsonText.replace(/```\n?/g, '').replace(/```\n?/g, '').trim();
    }

    // Parse JSON
    const extractedData = JSON.parse(jsonText);
    
    return extractedData;
  } catch (error) {
    console.error('Error extracting data with Gemini:', error);
    throw new Error(`Failed to extract data from PDF: ${error.message}`);
  }
};
